// react
import { MouseEvent, useContext, useState } from 'react';

// types
import { ActionButton, Ifr, IfrPayload, IfrStatus } from '../../../@types';

// i18n
import { Trans, useTranslation } from 'react-i18next';

// router
import { useSearchParams } from 'react-router-dom';

// context
import { ContextBanner } from '../../../App.context';
import { ContextIfr } from '../../pages/Project/Project.context';

// hooks
import { useFetch } from '../../../hooks/useFetch';

// mui
import { Alert, Grid, Typography } from '@mui/material';

// dialogs
import { DialogActions } from '../DialogActions/DialogActions';

// ui
import { BannerProps } from '../../ui/Banner/Banner';
import { Loading } from '../../ui/Loading/Loading';

// props
export interface DialogUpdateIfrStatusProps {
	isOpen: boolean;
	onClose: (e?: MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => void;
	onSuccess?: ({ ifr }: { ifr: Ifr }) => void;
}

export const DialogUpdateIfrStatus = ({
	isOpen,
	onClose,
	onSuccess,
}: DialogUpdateIfrStatusProps) => {
	// session
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');

	// hooks
	const [searchParams] = useSearchParams();
	const { t } = useTranslation();

	// params
	const paramDialogIfr = searchParams.get('dialogIfr');
	const paramStatus = searchParams.get('dialogStatus');

	// context
	const { setBanner } = useContext(ContextBanner);
	const { ifrTables } = useContext(ContextIfr);

	// state
	const [error, setError] = useState<string | null>(null);
	const [ifr, setIfr] = useState<Ifr | null>(null);

	// vars
	const isStatusFinalized = paramStatus === 'FINALIZED';

	// fetch ifr
	const { isLoading: isLoadingIfr } = useFetch({
		isEnabled: Boolean(paramDialogIfr && isOpen && !ifr),
		url: `${process.env.REACT_APP_API_URL}/ifrs/reports/${paramDialogIfr}`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				setIfr(res.data[0]);
			}
		},
	});

	// dialog config
	const dialog = {
		alert: t('alert.deleteIfr'),
		btnSubmit: {
			children: t('deleteReport'),
			color: 'error',
		},
		i18nKey: 'dialogUpdateIfrStatus.delete.content',
		title: t('deleteReport'),
	};

	if (isStatusFinalized) {
		dialog.alert = t('alert.finalizeIfr');
		dialog.btnSubmit = {
			children: t('finalizeReport'),
			color: 'primary',
		};
		dialog.i18nKey = 'dialogUpdateIfrStatus.finalize.content';
		dialog.title = t('finalizeReport');
	}

	const body: Partial<IfrPayload> = {
		status: paramStatus as IfrStatus,
	};

	const bannerSuccess: BannerProps = {
		hasClose: true,
		message: t('alert.ifrDeleted', { name: ifr?.name }),
		severity: 'success',
	};

	if (isStatusFinalized) {
		body.report = ifrTables;
		bannerSuccess.message = t('alert.ifrFinalized', { name: ifr?.name });
	}

	// patch ifr status
	const { fetchRequest: updateIfr, isLoading: isLoadingUpdateIfr } = useFetch({
		isLazy: true,
		url: `${process.env.REACT_APP_API_URL}/ifrs/reports/${ifr?.id}`,
		options: {
			method: 'PATCH',
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
			body: JSON.stringify(body),
		},
		onError: (error) => {
			let msg = t('error.default');

			if (error.id === 'IFR_UNAUTHORIZED_PATCH') {
				msg = t('error.unauthorized');
			}

			setError(msg);
		},
		onSuccess: (res) => {
			// set banner
			setBanner(bannerSuccess);

			// success callback
			onSuccess?.({ ifr: res.data });

			// close dialog callback
			onClose?.();
		},
	});

	// handlers
	const handleSubmit = (e: MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
		// clear error
		setError(null);

		// update ifr plan version
		updateIfr();
	};

	const handleClose = (e: MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
		// clear error
		setError(null);

		if (onClose) {
			onClose(e);
		}
	};

	const renderContent = () => {
		if (isLoadingIfr) {
			return (
				<Grid item xs={12}>
					<Grid alignItems="center" container justifyContent="center">
						<Grid item>
							<Loading />
						</Grid>
					</Grid>
				</Grid>
			);
		}

		return (
			<>
				<Grid item xs={12}>
					<Trans
						components={{
							strong: <Typography color="brand.purple.600" component="span" fontWeight={600} />,
						}}
						i18nKey={dialog.i18nKey}
						values={{ name: ifr?.name }}
					/>
				</Grid>
				<Grid item xs={12}>
					<Alert severity="warning">{dialog.alert}</Alert>
				</Grid>
				{error && (
					<Grid item xs={12}>
						<Alert severity="error">{error}</Alert>
					</Grid>
				)}
			</>
		);
	};

	return (
		<DialogActions
			actions={[
				{
					children: t('cancel'),
					onClick: handleClose,
					variant: 'text',
				},
				{
					...(dialog.btnSubmit as ActionButton),
					loading: isLoadingUpdateIfr,
					onClick: handleSubmit,
					variant: 'contained',
				},
			]}
			alignItems="center"
			IconCircleProps={{
				type: 'ifr',
			}}
			isOpen={isOpen}
			maxWidth={360}
			onClose={handleClose}
			subtitle={{
				children: ifr?.name,
			}}
			title={{
				children: dialog.title,
			}}>
			<Grid container spacing={2}>
				{renderContent()}
			</Grid>
		</DialogActions>
	);
};
